import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Field, formValueSelector } from "redux-form";
import { months } from "../../../../../../Utils/months";
import { renderDollar, renderCurrency, renderNumberKG, renderDollarDisabled, renderNumberKGDisabled, renderCurrencyDisabled } from "../../../../../../Utils/renderField/renderField";

const LatexTable = (props) => {

    const { fields, change, data_latex, parameters, currency, comparativoData } = props;

    const latex_data = (data_latex) ? data_latex : {};

    if (!latex_data) {
        return null;
    }

    const calculateMonthlyValues = () => {
        const getNumber = (val) => parseFloat(val) || 0;

        const tempData = {};

        for (let index = 0; index < months.length; index++) {
            const month = months[index];

            if (!latex_data[month]) continue;

            const prevMonth = index > 0 ? months[index - 1] : null;
            const current = latex_data[month];

            // Obtener campos usados en la fórmula
            const pp_costeado = current.pp_costeado || {};
            const inv_inicial = current.inv_inicial_de_pp || {};
            const costo_vta = current.costo_vta_del_mes || {};

            // Para meses posteriores a enero, heredamos inv_inicial_de_pp desde prod_termin_final del mes anterior
            let inv_kg = 0;
            let inv_valor = 0;

            if (index === 0) {
                inv_kg = getNumber(inv_inicial.kgs_secos);
                inv_valor = getNumber(inv_inicial.valor_total);
            } else if (prevMonth) {
                // Lógica mejorada: Verificar si el dato actual es igual al anterior (no ponderado)
                const comparativoLatexData = comparativoData && 
                    comparativoData.LatexProcesoData && 
                    comparativoData.LatexProcesoData.ProdTerminFinal && 
                    comparativoData.LatexProcesoData.ProdTerminFinal[prevMonth];
                
                const comparativoLatexDataActual = comparativoData && 
                    comparativoData.LatexProcesoData && 
                    comparativoData.LatexProcesoData.ProdTerminFinal && 
                    comparativoData.LatexProcesoData.ProdTerminFinal[month];
                
                // Verificar si está ponderado: si el mes actual es diferente al anterior
                const estaGrabadoElMes = comparativoLatexData && comparativoLatexDataActual &&
                    (Math.abs(getNumber(comparativoLatexData.KgSecos) - getNumber(comparativoLatexDataActual.KgSecos)) > 0.01 ||
                     Math.abs(getNumber(comparativoLatexData.CostoKgSec) - getNumber(comparativoLatexDataActual.CostoKgSec)) > 0.00001);
                
                if (comparativoLatexData && 
                    (getNumber(comparativoLatexData.KgSecos) > 0 || getNumber(comparativoLatexData.CostoKgSec) > 0) &&
                    estaGrabadoElMes) {
                    // Usar datos del comparativo si están disponibles, no son cero Y están ponderados
                    inv_kg = getNumber(comparativoLatexData.KgSecos);
                    const inv_costo = getNumber(comparativoLatexData.CostoKgSec);
                    inv_valor = inv_kg * inv_costo;
                    
                    // Actualizar los campos del formulario con los datos del comparativo
                    change(`latex_data.${month}.inv_inicial_de_pp.kgs_secos`, inv_kg);
                    change(`latex_data.${month}.inv_inicial_de_pp.costo_kg_seco`, inv_costo);
                    change(`latex_data.${month}.inv_inicial_de_pp.valor_total`, inv_valor);
                } else {
                    // Usar datos del presupuesto (prod_termin_final del mes anterior del mismo formulario)
                    const prevData = tempData[prevMonth] || data_latex[prevMonth] || {};
                    const prevProdTermFinal = prevData.prod_termin_final || {};
                    
                    inv_kg = getNumber(prevProdTermFinal.kgs_secos);
                    const inv_costo = getNumber(prevProdTermFinal.costo_kg_seco);
                    inv_valor = inv_kg * inv_costo;
                    
                    // Actualizar los campos del formulario con los datos del presupuesto
                    change(`latex_data.${month}.inv_inicial_de_pp.kgs_secos`, inv_kg);
                    change(`latex_data.${month}.inv_inicial_de_pp.costo_kg_seco`, inv_costo);
                    change(`latex_data.${month}.inv_inicial_de_pp.valor_total`, inv_valor);
                }
            }

            // Cálculos para disponible_para_vta
            const disponible_kg = getNumber(pp_costeado.kgs_secos) + inv_kg;
            const disponible_valor = getNumber(pp_costeado.valor_total) + inv_valor;
            const disponible_costo = disponible_kg > 0 ? disponible_valor / disponible_kg : 0;

            tempData[month] = {
                disponible_para_vta: {
                    kgs_secos: disponible_kg,
                    valor_total: disponible_valor,
                    costo_kg_seco: disponible_costo,
                },
                coagulos_centrifugado: {
                    kgs_secos: disponible_kg * getNumber(parameters.var_clot_centrifuged / 100),
                    valor_total: 0,
                    costo_kg_seco: 0,
                },
                perdida: {
                    kgs_secos: disponible_kg * getNumber(parameters.var_lost / 100),
                    valor_total: 0,
                    costo_kg_seco: 0,
                }
            };

            const { coagulos_centrifugado, perdida } = tempData[month];

            // inv_final_de_pp
            const inv_final_kg = disponible_kg - coagulos_centrifugado.kgs_secos - perdida.kgs_secos;
            const inv_final_valor = disponible_valor - coagulos_centrifugado.valor_total - perdida.valor_total;
            const inv_final_costo = inv_final_kg > 0 ? inv_final_valor / inv_final_kg : 0;

            tempData[month].inv_final_de_pp = {
                kgs_secos: inv_final_kg,
                valor_total: inv_final_valor,
                costo_kg_seco: inv_final_costo,
            };

            // prod_termin_final
            const costo_vta_kg = getNumber(costo_vta.kgs_secos);
            const costo_vta_valor = getNumber(costo_vta.valor_total);

            const final_kg = disponible_kg - coagulos_centrifugado.kgs_secos - perdida.kgs_secos - costo_vta_kg;
            const final_valor = disponible_valor - costo_vta_valor;
            const final_costo = final_kg > 0 ? final_valor / final_kg : 0;

            tempData[month].prod_termin_final = {
                kgs_secos: final_kg,
                valor_total: final_valor,
                costo_kg_seco: final_costo,
            };
        }

        // Aplicar todos los cambios al final
        for (let i = 0; i < months.length; i++) {
            let month = months[i];
            if (!tempData[month]) continue;

            let data = tempData[month];

            change(`latex_data.${month}.disponible_para_vta.kgs_secos`, data.disponible_para_vta.kgs_secos);
            change(`latex_data.${month}.disponible_para_vta.valor_total`, data.disponible_para_vta.valor_total);
            change(`latex_data.${month}.disponible_para_vta.costo_kg_seco`, data.disponible_para_vta.costo_kg_seco);

            change(`latex_data.${month}.coagulos_centrifugado.kgs_secos`, data.coagulos_centrifugado.kgs_secos);
            change(`latex_data.${month}.coagulos_centrifugado.valor_total`, 0);
            change(`latex_data.${month}.coagulos_centrifugado.costo_kg_seco`, 0);

            change(`latex_data.${month}.perdida.kgs_secos`, data.perdida.kgs_secos);
            change(`latex_data.${month}.perdida.valor_total`, 0);
            change(`latex_data.${month}.perdida.costo_kg_seco`, 0);

            change(`latex_data.${month}.inv_final_de_pp.kgs_secos`, data.inv_final_de_pp.kgs_secos);
            change(`latex_data.${month}.inv_final_de_pp.valor_total`, data.inv_final_de_pp.valor_total);
            change(`latex_data.${month}.inv_final_de_pp.costo_kg_seco`, data.inv_final_de_pp.costo_kg_seco);

            change(`latex_data.${month}.prod_termin_final.kgs_secos`, data.prod_termin_final.kgs_secos);
            change(`latex_data.${month}.prod_termin_final.valor_total`, data.prod_termin_final.valor_total);
            change(`latex_data.${month}.prod_termin_final.costo_kg_seco`, data.prod_termin_final.costo_kg_seco);
        }

    };

     const executedRef = useRef(false);
        
            useEffect(() => {
                if (executedRef.current) return;
            
                const hasLatexData = months.some((month) => {
                    const data = latex_data[month];
                    return data && Object.keys(data).length > 0;
                });
            
                if (hasLatexData) {
                    calculateMonthlyValues();
                    executedRef.current = true; 
                }
            }, [latex_data]);
    

    

    const rowDescriptionClassName = "text-left table-fixed-cell-left-0 bg-white text-nowrap tr--border-left tr--border-right";

    return (
        <div className="reports__table-bg">
            <div className="reports-table__container">
                <table className="table mb-0 text-center min-w-500">
                    <thead>
                        <tr>
                            <th rowSpan={2} className="text-left table-fixed-cell-left-0 tr--border-top tr--border-left td--shadow-right">&nbsp;</th>
                            {months.map((_, index) => (
                                <th key={index} colSpan={3} className={`th--border ${index === 0 ? "tr--no-border-left" : ""}`}>{index + 1}</th>
                            ))}
                        </tr>
                        <tr className="tr--no-radius tr--border-top">
                            {months.map((_, index) => (
                                <React.Fragment key={index}>
                                    <th className={index > 0 ? "tr--border-left" : ""}>Kgs Secos</th>
                                    <th>Costo Kg Sec</th>
                                    <th className="tr--border-right">Valor total</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {[
                            { label: "Entradas MP a producción", key: "entradas_mp_a_produccion", style: '' },
                            { label: "(+) Gtos. De compra de MP", key: "gtos_de_compra_de_mp", style: '' },
                            { label: "Sub-total materia prima", key: "sub_total_materia_prima", style: '' },
                            { label: "(-) SKIM ", key: "skim", style: '' },
                            { label: "(-) Coàgulo lavado de pilas/centrifugas", key: "coagulo_lavado_de_pilas_centrifugas" },
                            { label: "(-) Coàgulo de Trampas", key: "coagulo_de_trampas", style: '' },
                            { label: "(=) MP transf. En PP", key: "mp_transf_en_pp", style: 'celda-totales ' },
                            { label: "(+) Cto. De Proceso", key: "cto_de_proceso", style: '' },
                            { label: "(=) PP costeado", key: "pp_costeado", style: 'celda-totales ' },
                        ].map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className={row.style + rowDescriptionClassName}>{row.label}</td>
                                {months.map((month, index) => {
                                    const monthClass = index % 2 === 0 ? "" : " month-dark";

                                    return (
                                        <React.Fragment key={index}>
                                            <td className={row.style + monthClass}>
                                                <Field
                                                    name={`latex_data.${month}.${row.key}.kgs_secos`}
                                                    type="text"
                                                    component={renderNumberKGDisabled}
                                                    className="form-control"
                                                    placeholder='0.00'
                                                />
                                            </td>
                                            <td className={row.style + monthClass}>
                                                <Field
                                                    name={`latex_data.${month}.${row.key}.costo_kg_seco`}
                                                    type="text"
                                                    component={currency !== 'GTQ' ? renderDollarDisabled : renderCurrencyDisabled}
                                                    className="form-control"
                                                    decimalScale={5}
                                                    placeholder='$0.00'
                                                />
                                            </td>
                                            <td className={row.style + monthClass + ' tr--border-right'}>
                                                <Field
                                                    name={`latex_data.${month}.${row.key}.valor_total`}
                                                    type="text"
                                                    component={currency !== 'GTQ' ? renderDollarDisabled : renderCurrencyDisabled}
                                                    className="form-control"
                                                    placeholder='$0.00'
                                                />
                                            </td>
                                        </React.Fragment>
                                    )
                                })}
                            </tr>
                        ))}
                        <tr className="tr--border-top">
                            <td className={rowDescriptionClassName}>(+) Inv. Inicial de PP</td>
                            {months.map((month, index) => {
                                // Si no es enero, obtener el Inventario Final del mes anterior
                                const previousMonth = index > 0 ? months[index - 1] : null;
                                const monthClass = index % 2 === 0 ? "" : " month-dark";
                                return (
                                    <React.Fragment key={index}>
                                        <td className={monthClass}>
                                            {index === 0 ? (
                                                <Field
                                                    name={`latex_data.${month}.inv_inicial_de_pp.kgs_secos`}
                                                    type="text"
                                                    component={renderNumberKG}
                                                    className="form-control"
                                                    placeholder='0.0 kg'
                                                    isReport
                                                    onBlur={calculateMonthlyValues}
                                                />
                                            ) : (
                                                <Field
                                                    name={`latex_data.${month}.inv_inicial_de_pp.kgs_secos`}
                                                    type="text"
                                                    component={renderNumberKGDisabled}
                                                    className="form-control"
                                                    placeholder='0.0 kg'
                                                />
                                            )}
                                        </td>
                                        <td className={monthClass}>
                                            {index === 0 ? (
                                                <Field
                                                    name={`latex_data.${month}.inv_inicial_de_pp.costo_kg_seco`}
                                                    type="text"
                                                    component={currency !== 'GTQ' ? renderDollar : renderCurrency}
                                                    className="form-control"
                                                    placeholder="$ 0.00"
                                                    decimalScale={5}
                                                    onBlur={calculateMonthlyValues}
                                                />
                                                                        ) : (
                                <Field
                                    name={`latex_data.${month}.inv_inicial_de_pp.costo_kg_seco`}
                                    type="text"
                                    component={currency !== 'GTQ' ? renderDollarDisabled : renderCurrencyDisabled}
                                    className="form-control"
                                    placeholder="$ 0.00"
                                    decimalScale={5}
                                />
                            )}
                                        </td>
                                        <td className={monthClass + ' tr--border-right'}>
                                            {index === 0 ? (
                                                <Field
                                                    name={`latex_data.${month}.inv_inicial_de_pp.valor_total`}
                                                    type="text"
                                                    component={currency !== 'GTQ' ? renderDollarDisabled : renderCurrencyDisabled}
                                                    className="form-control"
                                                    placeholder="$ 0.00"
                                                />
                                                                        ) : (
                                <Field
                                    name={`latex_data.${month}.inv_inicial_de_pp.valor_total`}
                                    type="text"
                                    component={currency !== 'GTQ' ? renderDollarDisabled : renderCurrencyDisabled}
                                    className="form-control"
                                    placeholder="$ 0.00"
                                />
                            )}
                                        </td>

                                    </React.Fragment>
                                );
                            })}
                        </tr>


                        {[

                            { label: "(=) Disponible para Vta", key: "disponible_para_vta", style: 'celda-totales ' },
                            { label: "(-) Coagulos centrifugado", key: "coagulos_centrifugado", style: '' },
                            { label: "(-) Pérdida", key: "perdida", style: '' },
                            { label: "(=) Inv. Final de PP", key: "inv_final_de_pp", style: 'celda-totales ' },
                            { label: "(-) Costo Vta. Del Mes", key: "costo_vta_del_mes", style: '' },
                            { label: "(=) Prod. Termin. Final", key: "prod_termin_final", style: 'celda-totales ' }
                        ].map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className={row.style + rowDescriptionClassName}>{row.label}</td>
                                {months.map((month, index) => {
                                    const monthClass = index % 2 === 0 ? "" : " month-dark";
                                    return (
                                        <React.Fragment key={index}>
                                            <td className={row.style + monthClass}>
                                                <Field
                                                    name={`latex_data.${month}.${row.key}.kgs_secos`}
                                                    type="text"
                                                    component={renderNumberKGDisabled}
                                                    className="form-control"
                                                    placeholder='0.00'
                                                />
                                            </td>
                                            <td className={row.style + monthClass}>
                                                <Field
                                                    name={`latex_data.${month}.${row.key}.costo_kg_seco`}
                                                    type="text"
                                                    component={currency !== 'GTQ' ? renderDollarDisabled : renderCurrencyDisabled}
                                                    className="form-control"
                                                    decimalScale={5}
                                                    placeholder='$0.00'
                                                />
                                            </td>
                                            <td className={row.style + monthClass + ' tr--border-right'}>
                                                <Field
                                                    name={`latex_data.${month}.${row.key}.valor_total`}
                                                    type="text"
                                                    component={currency !== 'GTQ' ? renderDollarDisabled : renderCurrencyDisabled}
                                                    className="form-control"
                                                    placeholder='$0.00'
                                                />
                                            </td>
                                        </React.Fragment>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const formName = 'CostoVentasForm';

const selector = formValueSelector(formName);

const mapStateToProps = (state) => {
    const data_latex = selector(state, 'latex_data');
    const parameters = selector(state, 'parameters');
    const comparativoData = state.costoDeVentas.comparativoData;
    return {
        data_latex,
        parameters,
        comparativoData
    };
};


export default connect(mapStateToProps)(LatexTable);
